/* eslint-disable consistent-return */
import { IconContentLayoutList } from '@doltech/icons/IconContentLayoutList';
import { IconContentLayoutNum } from '@doltech/icons/IconContentLayoutNum';
import * as React from 'react';
import { ReactEditor } from 'slate-react';

import { setDefaults } from '../../../../slatePlugins/common/utils/setDefaults';
import { ToolbarList } from '../../../../slatePlugins/elements/list/components/ToolbarList';
import {
  ELEMENT_LI,
  ELEMENT_OL,
  ELEMENT_UL,
} from '../../../../slatePlugins/elements/list/defaults';
import { ListPlugin } from '../../../../slatePlugins/elements/list/ListPlugin';
import { normalizeListItem } from '../../../../slatePlugins/elements/list/normalizers/normalizeListItem';
import { WithListOptions } from '../../../../slatePlugins/elements/list/types';
import { withList } from '../../../../slatePlugins/elements/list/withList';
import { DEFAULTS_LIST } from '../../../../slatePlugins/slateBulletList.types';
import { ListItemElement } from './element/ListItemElement';
import { OrderedListElement } from './element/OrderedListElement';
import { UnorderedListElement } from './element/UnorderedListElement';

const DEFAULT_ICON_SIZE = {
  root: [
    {
      selectors: {
        '> svg': {
          display: 'block',
          width: '24px',
          height: '24px',
        },
      },
    },
  ],
};

/**
 * @param validLiChildrenTypes
 * @param options
 */
const withListPlugin =
  ({ validLiChildrenTypes, ...options }: WithListOptions = {}) =>
  <T extends ReactEditor>(editor: T) => {
    const { normalizeNode } = editor;
    const { li } = setDefaults(options, DEFAULTS_LIST);

    // Overriding withList normalizeNode function
    editor = withList({ validLiChildrenTypes, ...options })(editor);
    editor.normalizeNode = (entry) => {
      const [node, path] = entry as [any, any[]];
      if (
        node.type === li.type &&
        normalizeListItem(editor, { nodeEntry: [node, path], validLiChildrenTypes }, options)
      ) {
        // Tree changed - kick off another normalization
        return;
      }

      normalizeNode(entry);
    };

    return editor;
  };

const OverrideListPlugin = (options = {}) => {
  const overridePlugin = ListPlugin(options);
  overridePlugin.renderElement = (props) => {
    const { element } = props;
    switch (element?.type) {
      case ELEMENT_UL: {
        return <UnorderedListElement {...options} {...props} />;
      }
      case ELEMENT_OL: {
        return <OrderedListElement {...options} {...props} />;
      }
      case ELEMENT_LI: {
        return <ListItemElement {...options} {...props} />;
      }
      default:
    }
  };

  return overridePlugin;
};

export const SlateListPlugin = {
  plugin: OverrideListPlugin,
  withPlugin: withListPlugin,
  toolbar: (options = DEFAULTS_LIST) => (
    <>
      <ToolbarList
        {...options.ul}
        styles={DEFAULT_ICON_SIZE}
        typeList={options.ul.type}
        icon={<IconContentLayoutList />}
      />
      <ToolbarList
        {...options.ol}
        styles={DEFAULT_ICON_SIZE}
        typeList={options.ol.type}
        icon={<IconContentLayoutNum />}
      />
    </>
  ),
};
